/**
 * The Circles panel: how this calendar is organised, and how this view draws it.
 *
 * It replaces the Sources panel as the primary organiser — sources are a
 * section at the bottom of it now, because where an entry came from is a
 * smaller question than which circle it is in.
 *
 * The two halves of a row go to different places (docs/CIRCLES.md §6). Name,
 * colour, weight and style are the **circle's** — they are data, the same in
 * every view — and a `PATCH /api/tags/:id` followed by a refetch is how they
 * change, at once. Placement and visibility are the **view's**: they go to the
 * scene, which tweens the rings there, and into the working layout, which is
 * written to the view only when someone presses Save on the views bar. A
 * viewer gets the second half and not the first — they can arrange the owner's
 * circles however they like, in their own browser, and nothing they do is
 * saved.
 *
 * Ordering and nesting are drag-and-drop, with the grip doubling as the
 * keyboard's way in: arrow keys move a circle up, down, in and out. A finger
 * has neither, so the disclosure carries the same four moves as buttons. All
 * three go through the same pure moves in `circles.ts`, which enforce the
 * depth limit the worker enforces too, so an impossible move is refused
 * before it is sent.
 */
import { placementOf, type CalEvent, type ChildPlacement, type Source, type Tag, type ViewLayout } from '@circle/shared';
import { api } from '../app/api';
import { DEFAULT_THEME_NAME, themes } from '../fragments/types';
import { append as appendAll, button, el, selectField, setChildren, type Child } from './dom';
import { confirmDialog, openDialog } from './dialog';
import { toast, toastError } from './toast';
import { sourcesSection, type SourcesSection } from './sources-panel';
import { circleShareSection } from './circle-share';
import { CIRCLE_COLOURS, nextColour } from './colours';
import type { Panel } from './panel';
import type { PendingQueue } from './pending';
import {
  PLACEMENTS,
  PROMINENCE_STEPS,
  ROOT_NAME,
  ROOT_RING_ID,
  buildCircleTree,
  circleErrorMessage,
  dropOn,
  findNode,
  flattenTree,
  formatProminence,
  indentUnderPrevious,
  moveWithinParent,
  nodeOf,
  outdentToGrandparent,
  parseProminence,
  prominenceFromSlider,
  prominenceToSlider,
  withNode,
  type CircleNode,
  type TagMove,
  type WorkingLayout,
} from './circles';

const AVATAR_SHAPES = [
  { value: '', label: 'Inherit' },
  { value: 'circle', label: 'Round' },
  { value: 'square', label: 'Square' },
  { value: 'none', label: 'No photo' },
];

export interface CirclesPanelOptions {
  panel: Panel;
  calendarId: string;
  /** Read live: a reload replaces the page's arrays wholesale. */
  tags: () => Tag[];
  events: () => CalEvent[];
  sources: () => Source[];
  /** The layout in play, and where a change to it goes. */
  working: WorkingLayout;
  /** Owners edit the circles; viewers only rearrange them, for themselves. */
  editable: boolean;
  /** Refetch the bundle and re-render the ring. */
  reload: () => Promise<void>;
  /**
   * The page's moderation queue. The sources section is drawn only where it is
   * given: an owner's full panel has one, the dock and a viewer's panel do not.
   */
  pending?: PendingQueue;
  /**
   * The dock's form: swatch, name, count and eye per circle, and nothing
   * else — no grip, no modes, no disclosure, no sources. The same tree drawn
   * small enough to stay on screen (ui/circles-dock.ts).
   */
  compact?: boolean;
  /**
   * Compact only: the chevron on a row was pressed; open the full panel on
   * that circle. The name no longer does this — it filters (`onFilter`) — so
   * that a viewer, who has nothing to open, gets the same press as an owner.
   */
  onOpen?: (circleId: string) => void;
  /** Compact only: a name was pressed; show this circle alone, or all again. */
  onFilter?: (circleId: string) => void;
  /** Compact only: which circle the filter is on, asked afresh at every paint. */
  filteredCircle?: () => string | null;
  /** Start with this circle's disclosure open — the dock hands over this way. */
  expand?: string;
}

export const CIRCLES_PANEL_KEY = 'circles';

/**
 * Open it, or close it if it is already the thing on screen — the same second-
 * click-closes contract every other panel button has.
 */
export function openCirclesPanel(options: CirclesPanelOptions): void {
  const { panel } = options;
  // Asked for a particular circle while already open: stay open, go there.
  if (panel.openKey === CIRCLES_PANEL_KEY && !options.expand) {
    panel.close();
    return;
  }
  const view = new CirclesView(options);
  panel.open(
    { title: 'Circles', body: [view.root] },
    { key: CIRCLES_PANEL_KEY, onClose: () => view.destroy() },
  );
}

/** The panel's body, built so a test can mount it without a Panel. */
export class CirclesView {
  readonly root: HTMLElement;
  private readonly options: CirclesPanelOptions;
  private readonly tree: HTMLElement;
  /** Rows whose disclosure is open, so a repaint does not shut them again. */
  private readonly expanded = new Set<string>();
  private dragging: string | null = null;
  private readonly unwatch: () => void;
  private sources: SourcesSection | undefined;

  constructor(options: CirclesPanelOptions) {
    this.options = options;
    if (options.expand) this.expanded.add(options.expand);
    // A plain list, on purpose: the tree pattern promises arrow keys that move
    // *between* items, and here the arrow keys on a grip move the item itself.
    this.tree = el('ul', {
      class: options.compact ? 'circles circles--compact' : 'circles',
      'aria-label': 'Circles',
    });
    this.root = el('div', { class: options.compact ? 'circles-panel circles-panel--compact' : 'stack circles-panel' });
    // The modes are drawn from the working layout, and Revert and a view switch
    // both change it from out here. Without this the segmented control would go
    // on claiming a mode the ring had already stopped drawing.
    this.unwatch = options.working.watch(() => this.paintTree());
    this.paint();
  }

  destroy(): void {
    this.unwatch();
    this.sources?.destroy();
  }

  /** Rebuild from whatever the page's arrays say now. */
  paint(): void {
    this.paintTree();
    if (this.options.compact) {
      setChildren(this.root, [this.tree]);
      return;
    }
    const body: Child[] = [
      el(
        'div',
        { class: 'section__head' },
        el('p', {
          class: 'small muted',
          text: this.options.editable
            ? 'A circle is a group of people. Give one a ring of its own, fold it back in, or leave it out of this view.'
            : 'These are the owner’s circles. Rearranging them here changes only what you see.',
        }),
        this.options.editable
          ? button('Add circle', () => void this.addCircle(null), { class: 'btn--small' })
          : null,
      ),
      this.tree,
      this.options.editable && this.options.tags().length === 0 ? this.emptyTree() : null,
    ];
    this.sources?.destroy();
    this.sources = undefined;
    if (this.options.editable && this.options.pending) {
      this.sources = sourcesSection({
        calendarId: this.options.calendarId,
        sources: this.options.sources,
        events: this.options.events,
        tags: this.options.tags,
        pending: this.options.pending,
        reload: this.options.reload,
      });
      body.push(
        el('hr', { class: 'rule' }),
        el('div', { class: 'section__head' }, el('h2', { text: 'Where entries come from' })),
        this.sources.root,
      );
    }
    setChildren(this.root, body);
  }

  /**
   * A calendar with no circles yet. The root row alone says nothing about what
   * a circle *is*, and its mode control has nothing to act on, so this says it
   * and offers the one thing that makes the rest of the panel mean something.
   */
  private emptyTree(): HTMLElement {
    return el(
      'div',
      { class: 'empty circles__empty', 'data-role': 'empty-tree' },
      el('p', {
        text: 'No circles yet. A circle is a group of people on the ring — family, work, whoever else — with a colour of its own. It can be drawn as a band on the ring, a ring inside it, or a ring of its own.',
      }),
      button('Add circle', () => void this.addCircle(null), { class: 'btn--small btn--primary' }),
    );
  }

  private paintTree(): void {
    const root = buildCircleTree(this.options.tags());
    // The dock hangs under the calendar's name, which already says "Everyone":
    // a row for the root there would name the ring twice. The panel keeps it,
    // since the root's own eye and count live nowhere else.
    const rows = this.options.compact
      ? root.children.map((child) => this.row(child, root))
      : [this.row(root, root)];
    setChildren(this.tree, rows);
  }

  // --- one row --------------------------------------------------------------

  private row(node: CircleNode, tree: CircleNode): HTMLElement {
    const isRoot = node.id === ROOT_RING_ID;
    const view = nodeOf(this.options.working.current, node.id);
    const compact = this.options.compact === true;
    const editable = this.options.editable && !isRoot && !compact;

    const filtered = compact && this.options.filteredCircle?.() === node.id;
    const li = el('li', {
      class: 'circle',
      'data-circle': node.id,
      'data-depth': String(node.depth),
      'data-visible': view.visible ? 'true' : 'false',
      ...(filtered ? { 'data-filtered': 'true' } : {}),
    });
    li.style.setProperty('--depth', String(node.depth));

    const head = el('div', { class: 'circle__row' });
    appendAll(head, [
      editable ? this.grip(node) : null,
      this.swatch(node, editable),
      compact
        ? this.compactName(node, isRoot, filtered)
        : isRoot
          ? el('span', { class: 'circle__name circle__name--root', text: ROOT_NAME })
          : this.nameField(node),
      el('span', { class: 'circle__count', text: String(this.countIn(node)) }),
      node.tag?.shareToken ? linkedBadge(node.tag.name) : null,
      this.eye(node, view.visible),
      this.options.editable && !compact ? this.moreButton(node) : null,
      compact && !isRoot && this.options.onOpen ? this.openButton(node) : null,
    ]);
    li.appendChild(head);

    if (!compact) {
      // How this circle sits on its parent is the circle's own to say, so the
      // control is on its row. The root sits on nothing and gets none.
      if (!isRoot) li.appendChild(this.placement(node, tree));
      if (this.options.editable) li.appendChild(this.details(node, isRoot));
      if (editable) this.wireDropTarget(li, node.id);
    }

    if (node.children.length > 0) {
      // A nested list needs a name of its own, or a screen reader announces
      // "list, 3 items" with nothing to say which circle they are inside.
      const kids = el('ul', {
        class: 'circles__kids',
        'aria-label': `Inside ${node.tag?.name ?? ROOT_NAME}`,
      });
      for (const child of node.children) kids.appendChild(this.row(child, tree));
      li.appendChild(kids);
    }
    return li;
  }

  /**
   * The dock's name: the press that shows this circle and puts the rest aside.
   *
   * It used to open the full panel, which was an owner's press and left a
   * viewer with plain text and nothing to do. Filtering is the thing both of
   * them want from a legend — "just Family" — so the name does that for
   * everyone, and editing moved to the chevron beside it.
   */
  private compactName(node: CircleNode, isRoot: boolean, filtered: boolean): HTMLElement {
    const name = node.tag?.name ?? ROOT_NAME;
    const cls = isRoot ? 'circle__name circle__name--root' : 'circle__name';
    if (!this.options.onFilter || isRoot) return el('span', { class: cls, text: name });
    return el('button', {
      class: `${cls} circle__name--button`,
      type: 'button',
      'data-role': 'filter',
      'aria-pressed': filtered ? 'true' : 'false',
      title: filtered ? `Showing ${name} only` : `Show ${name} only`,
      text: name,
      onClick: () => this.options.onFilter?.(node.id),
    });
  }

  /** The dock's way into the panel, now that the name is the filter. */
  private openButton(node: CircleNode): HTMLElement {
    const name = node.tag?.name ?? ROOT_NAME;
    return el('button', {
      class: 'circle__open',
      type: 'button',
      'data-role': 'open',
      'aria-label': `Open ${name} in Circles`,
      title: `Open ${name} in Circles`,
      text: '›',
      onClick: () => this.options.onOpen?.(node.id),
    });
  }

  /** Opens the row's disclosure — colour, weight, style, and the two actions. */
  private moreButton(node: CircleNode): HTMLElement {
    const open = this.expanded.has(node.id);
    return el(
      'button',
      {
        class: 'circle__disclose',
        type: 'button',
        'data-role': 'more',
        'aria-expanded': open ? 'true' : 'false',
        'aria-label': `Settings for ${node.tag?.name ?? ROOT_NAME}`,
        onClick: () => this.toggleDetails(node.id),
      },
      chevronGlyph(open),
    );
  }

  /** Entries in this circle or any circle inside it, however deep the tree goes. */
  private countIn(node: CircleNode): number {
    if (node.id === ROOT_RING_ID) return this.options.events().length;
    const ids = new Set(flattenTree(node).map((n) => n.id));
    return this.options.events().filter((e) => (e.tagIds ?? []).some((id) => ids.has(id))).length;
  }

  /**
   * The mark for a circle is a ring, not a square of paint: the whole product
   * draws groups as rings, and a swatch that says so is one less thing to
   * learn. It opens the colour editor for an owner and is decoration otherwise.
   */
  private swatch(node: CircleNode, editable: boolean): HTMLElement {
    const colour = node.tag?.colour ?? '';
    const glyph = ringGlyph(colour);
    if (!editable) return el('span', { class: 'circle__swatch', 'aria-hidden': 'true' }, glyph);
    const btn = el('button', {
      class: 'circle__swatch',
      type: 'button',
      'data-role': 'colour',
      'aria-label': `Colour of ${node.tag?.name ?? ROOT_NAME}`,
      onClick: () => this.toggleDetails(node.id),
    });
    btn.appendChild(glyph);
    return btn;
  }

  private nameField(node: CircleNode): HTMLElement {
    const tag = node.tag!;
    if (!this.options.editable) return el('span', { class: 'circle__name', text: tag.name });
    const input = el('input', {
      class: 'circle__name',
      value: tag.name,
      maxlength: '60',
      spellcheck: 'false',
      'aria-label': `Name of ${tag.name}`,
    });
    const commit = (): void => {
      const next = input.value.trim();
      if (!next || next === tag.name) {
        input.value = tag.name;
        return;
      }
      void this.patchTag(tag, { name: next }, () => {
        input.value = tag.name;
      });
    };
    input.addEventListener('blur', commit);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        input.blur();
      }
      if (event.key === 'Escape') {
        input.value = tag.name;
        input.blur();
      }
    });
    return input;
  }

  /**
   * Visibility, which is the view's and not the circle's — so a viewer gets it
   * too. Hiding a circle hides what is in it rather than tipping it back onto
   * the root, which is what the scene does with it.
   */
  private eye(node: CircleNode, visible: boolean): HTMLElement {
    const name = node.tag?.name ?? ROOT_NAME;
    return el(
      'button',
      {
        class: 'circle__eye',
        type: 'button',
        'data-role': 'visible',
        'aria-pressed': visible ? 'true' : 'false',
        'aria-label': visible ? `Hide ${name}` : `Show ${name}`,
        title: visible ? 'Hide' : 'Show',
        onClick: () => this.setNode(node.id, { visible: !visible }),
      },
      eyeGlyph(visible),
    );
  }

  /**
   * Band on the parent · ring inside it · ring of its own, drawn as well as
   * named — the sentence a person already has in their head about *this*
   * circle, on this circle's row (docs/CIRCLES.md §9).
   *
   * Each button carries a diagram of where the circle goes — a band on one
   * ring, a ring inside a ring, a ring out on an orbit. The words alone were
   * three abstractions in a row; the drawings are the fastest thing on the
   * panel to read, and they are the same shapes that appear on the ring a
   * moment later. What is drawn is the circle's *effective* placement: its own
   * choice, or, while it has made none, what its parent's mode hands it.
   *
   * One tab stop: the checked option is the only one in the tab order, and
   * ←/→ (or ↑/↓) move the choice as a radio group does, applying it as they go.
   * The roving tabindex is written out here rather than taken from a shared
   * helper; if `ui/segmented.ts` lands, the three attributes and the keydown
   * below are what it replaces.
   *
   * The visible word is one or two — the row is indented and the diagram is
   * what gets read first — so the whole sentence, parent named, is the option's
   * accessible name and its tooltip: "A band on Family", "A ring inside
   * Family", "A ring of its own, on an orbit round Family".
   */
  private placement(node: CircleNode, tree: CircleNode): HTMLElement {
    const parentId = node.parentId ?? ROOT_RING_ID;
    const parent = findNode(tree, parentId);
    const parentName = parent?.tag?.name ?? ROOT_NAME;
    const own = nodeOf(this.options.working.current, node.id);
    const current = placementOf(own, nodeOf(this.options.working.current, parentId));
    const name = node.tag?.name ?? ROOT_NAME;

    const group = el('div', {
      class: 'segmented',
      role: 'radiogroup',
      'aria-label': `How ${name} sits on ${parentName}`,
    });
    for (const option of PLACEMENTS) {
      const active = option.value === current;
      group.appendChild(
        el(
          'button',
          {
            class: 'segmented__option',
            type: 'button',
            role: 'radio',
            'data-placement': option.value,
            'aria-checked': active ? 'true' : 'false',
            tabindex: active ? '0' : '-1',
            title: option.hint(parentName),
            'aria-label': option.hint(parentName),
            onClick: () => this.setNode(node.id, { placement: option.value }),
          },
          placementGlyph(option.value),
          el('span', { text: option.label }),
        ),
      );
    }
    group.addEventListener('keydown', (event) => {
      const delta = arrowDelta(event.key);
      if (delta === 0) return;
      event.preventDefault();
      const at = PLACEMENTS.findIndex((option) => option.value === current);
      const last = PLACEMENTS.length - 1;
      const next =
        Math.abs(delta) > 1
          ? PLACEMENTS[delta < 0 ? 0 : last]!
          : PLACEMENTS[(at + delta + PLACEMENTS.length) % PLACEMENTS.length]!;
      this.setNode(node.id, { placement: next.value });
      // The tree has been repainted under us; the focus goes to the new row's
      // checked option, which is the one tab stop this control has.
      this.tree
        .querySelector<HTMLElement>(
          `[data-circle="${cssEscape(node.id)}"] > .segmented [aria-checked="true"]`,
        )
        ?.focus();
    });
    return group;
  }

  // --- the disclosure -------------------------------------------------------

  private details(node: CircleNode, isRoot: boolean): HTMLElement {
    const open = this.expanded.has(node.id);
    const wrap = el('div', { class: 'circle__more', hidden: !open });
    if (!open) return wrap;

    setChildren(wrap, [
      isRoot ? null : this.colourEditor(node),
      isRoot ? null : this.prominenceSlider(node),
      isRoot ? null : this.styleFields(node),
      isRoot ? null : this.shareSection(node),
      isRoot ? null : this.moveActions(node),
      el(
        'div',
        { class: 'row circle__actions' },
        button('Add circle inside', () => void this.addCircle(isRoot ? null : node.id), {
          class: 'btn--small btn--quiet',
        }),
        isRoot
          ? null
          : button('Delete', () => void this.deleteCircle(node), { class: 'btn--small btn--danger' }),
      ),
    ]);
    return wrap;
  }

  /**
   * The grip's four moves as buttons, for a finger. HTML5 drag-and-drop does
   * not fire on a touch screen and there are no arrow keys on a phone, so
   * without these a circle could be made on a phone and never moved. A move
   * the tree refuses is drawn disabled rather than left to fail on press.
   */
  private moveActions(node: CircleNode): HTMLElement {
    const tree = buildCircleTree(this.options.tags());
    const move = (label: string, moves: TagMove[], role: string): HTMLElement =>
      button(label, () => void this.applyMoves(moves), {
        class: 'btn--small btn--quiet',
        'data-role': role,
        disabled: moves.length === 0,
      });
    const targets = moveTargets(tree, node.id);
    return el(
      'div',
      { class: 'field' },
      el('span', { class: 'small muted', text: 'Place' }),
      el(
        'div',
        { class: 'row circle__moves' },
        move('Move up', moveWithinParent(tree, node.id, -1), 'move-up'),
        move('Move down', moveWithinParent(tree, node.id, 1), 'move-down'),
        move('Move out', outdentToGrandparent(tree, node.id), 'move-out'),
        button('Move into…', () => this.openMoveInto(node, targets), {
          class: 'btn--small btn--quiet',
          'data-role': 'move-into',
          disabled: targets.length === 0,
        }),
      ),
    );
  }

  /** Pick the circle to put this one inside, from those that would take it. */
  private openMoveInto(node: CircleNode, targets: CircleNode[]): void {
    const name = node.tag?.name ?? ROOT_NAME;
    // A figure space per level keeps the tree's shape inside a flat <select>.
    const indent = (depth: number): string => '  '.repeat(Math.max(0, depth - 1));
    const pick = selectField({
      label: 'Inside',
      name: 'move-into',
      value: targets[0]?.id ?? '',
      options: targets.map((t) => ({ value: t.id, label: `${indent(t.depth)}${t.tag?.name ?? ROOT_NAME}` })),
    });
    const handle = openDialog({
      title: `Move ${name} into…`,
      description:
        'Only circles with room for it are offered: circles go three deep, and none can go inside itself.',
      body: [pick.root],
    });
    const cancel = button('Cancel', () => handle.close());
    const go = button(
      'Move',
      () => {
        const moves = dropOn(buildCircleTree(this.options.tags()), node.id, pick.input.value, 'into');
        handle.close();
        if (moves.length === 0) {
          toast(circleErrorMessage('TAG_DEPTH')!, 'error');
          return;
        }
        void this.applyMoves(moves);
      },
      { class: 'btn--primary' },
    );
    handle.foot.append(el('span', { class: 'grow' }), cancel, go);
    handle.root.appendChild(handle.foot);
  }

  private toggleDetails(id: string): void {
    if (this.expanded.has(id)) this.expanded.delete(id);
    else this.expanded.add(id);
    this.paintTree();
  }

  private colourEditor(node: CircleNode): HTMLElement {
    const tag = node.tag!;
    const hex = el('input', {
      class: 'circle__hex mono',
      value: tag.colour ?? '',
      placeholder: 'inherit',
      spellcheck: 'false',
      maxlength: '7',
      'aria-label': `Colour of ${tag.name}`,
    });
    const set = (colour: string | null): void => {
      void this.patchTag(tag, { colour });
    };
    hex.addEventListener('change', () => {
      const value = hex.value.trim();
      if (!value) return set(null);
      if (!/^#[0-9a-f]{6}$/i.test(value)) {
        hex.value = tag.colour ?? '';
        toast('A colour is six hex digits after a #, like #c98f4b.', 'error');
        return;
      }
      set(value);
    });

    const presets = el('div', { class: 'circle__presets' });
    for (const colour of CIRCLE_COLOURS) {
      presets.appendChild(
        el('button', {
          class: 'circle__preset',
          type: 'button',
          'data-colour': colour,
          'aria-label': colour,
          'aria-pressed': tag.colour?.toLowerCase() === colour ? 'true' : 'false',
          style: `--preset:${colour}`,
          onClick: () => set(colour),
        }),
      );
    }

    return el(
      'div',
      { class: 'field' },
      el('span', { class: 'small muted', text: 'Colour' }),
      el(
        'div',
        { class: 'circle__colour' },
        presets,
        hex,
        button('Inherit', () => set(null), { class: 'btn--small btn--quiet', 'data-role': 'inherit' }),
      ),
    );
  }

  /**
   * The weight: a log slider that lands on named stops, and a readout you can
   * type into when none of them is the number you meant.
   */
  private prominenceSlider(node: CircleNode): HTMLElement {
    const tag = node.tag!;
    const readout = el('input', {
      class: 'circle__prom-value mono',
      type: 'text',
      inputmode: 'decimal',
      size: '5',
      value: formatProminence(tag.prominence),
      'aria-label': `Weight of ${tag.name}, as a number`,
    });
    const slider = el('input', {
      class: 'circle__prom',
      type: 'range',
      min: '0',
      max: String(PROMINENCE_STEPS),
      step: '1',
      value: String(prominenceToSlider(tag.prominence)),
      'aria-label': `Weight of ${tag.name}`,
    });

    const restore = (): void => {
      slider.value = String(prominenceToSlider(tag.prominence));
      readout.value = formatProminence(tag.prominence);
    };
    const commit = (prominence: number): void => {
      if (prominence === tag.prominence) {
        restore();
        return;
      }
      void this.patchTag(tag, { prominence }, restore);
    };

    slider.addEventListener('input', () => {
      readout.value = formatProminence(prominenceFromSlider(Number(slider.value)));
    });
    slider.addEventListener('change', () => commit(prominenceFromSlider(Number(slider.value))));

    // Typed values are clamped but not snapped: this is the way to ×1.52.
    readout.addEventListener('change', () => {
      const typed = parseProminence(readout.value);
      if (typed === null) {
        restore();
        toast('A weight is a number between ×0.25 and ×4.', 'error');
        return;
      }
      slider.value = String(prominenceToSlider(typed));
      readout.value = formatProminence(typed);
      commit(typed);
    });
    readout.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        readout.blur();
      }
    });

    return el(
      'div',
      { class: 'field' },
      el('span', { class: 'small muted', text: 'Weight' }),
      el('div', { class: 'circle__weight' }, slider, readout),
      el('div', {
        class: 'field__hint',
        text: 'Louder circles get bigger marks, wider bands and the outer rings. The slider lands on round numbers; type one to say something else.',
      }),
    );
  }

  /**
   * Per-circle style overrides, from whatever the active theme offers. A theme
   * with no alternates has nothing to choose, so the two variant selects are
   * simply not drawn; the avatar shape is always there.
   */
  private styleFields(node: CircleNode): HTMLElement {
    const tag = node.tag!;
    // The registry rather than `getTheme`, which throws where no theme has been
    // imported: a panel with nothing to offer should show no select, not fail.
    const variants = themes[DEFAULT_THEME_NAME]?.variants;
    const style = tag.style ?? {};
    const fields: Child[] = [];

    const variantSelect = (
      kind: 'ringMark' | 'float',
      label: string,
      names: string[],
    ): HTMLElement | null => {
      if (names.length < 2) return null;
      const select = el(
        'select',
        { class: 'circle__variant', 'data-variant': kind, 'aria-label': label },
        ...names.map((name) =>
          el('option', { value: name, text: name === 'default' ? 'Default' : name }),
        ),
      );
      select.value = (style[kind] as string | undefined) ?? 'default';
      select.addEventListener('change', () => {
        void this.patchTag(tag, { style: { ...style, [kind]: select.value } });
      });
      return el('label', { class: 'field field--inline' }, el('span', { class: 'small muted', text: label }), select);
    };

    fields.push(
      variantSelect('ringMark', 'Mark', ['default', ...Object.keys(variants?.ringMark ?? {})]),
      variantSelect('float', 'Card', ['default', ...Object.keys(variants?.float ?? {})]),
    );

    const shape = el(
      'select',
      { class: 'circle__variant', 'data-variant': 'avatarShape', 'aria-label': 'Photos' },
      ...AVATAR_SHAPES.map((o) => el('option', { value: o.value, text: o.label })),
    );
    shape.value = style.avatarShape ?? '';
    shape.addEventListener('change', () => {
      const next = { ...style };
      if (shape.value) next.avatarShape = shape.value as 'circle' | 'square' | 'none';
      else delete next.avatarShape;
      void this.patchTag(tag, { style: next });
    });
    fields.push(
      el('label', { class: 'field field--inline' }, el('span', { class: 'small muted', text: 'Photos' }), shape),
    );

    return el('div', { class: 'circle__style' }, ...fields);
  }

  /**
   * The circle's own link (docs/CIRCLES.md §12). The whole ring's link lives
   * in the Share dialog; this one lives with the circle, because that is where
   * you are when you decide a group of people deserves a ring of their own.
   */
  private shareSection(node: CircleNode): HTMLElement {
    return circleShareSection({
      calendarId: this.options.calendarId,
      tag: node.tag!,
      onUpdated: async () => {
        await this.options.reload();
        this.paintTree();
      },
    });
  }

  // --- reordering -----------------------------------------------------------

  /**
   * The grip: dragged with a pointer, driven with the arrow keys. One control
   * for both, so what a mouse can do a keyboard can do, and neither is a
   * second-class path bolted on afterwards.
   */
  private grip(node: CircleNode): HTMLElement {
    const tag = node.tag!;
    const grip = el('button', {
      class: 'circle__grip',
      type: 'button',
      draggable: 'true',
      'data-role': 'grip',
      'aria-label': `Move ${tag.name}`,
      title: 'Drag to move, or use the arrow keys',
    });
    grip.appendChild(gripGlyph());

    grip.addEventListener('keydown', (event) => {
      const tree = buildCircleTree(this.options.tags());
      const moves = this.movesFor(tree, node.id, event.key);
      if (!moves) return;
      event.preventDefault();
      if (moves.length === 0) {
        toast(refusalFor(event.key, tree, node), 'error');
        return;
      }
      void this.applyMoves(moves);
    });

    grip.addEventListener('dragstart', (event) => {
      this.dragging = node.id;
      (event as DragEvent).dataTransfer?.setData('text/plain', node.id);
    });
    grip.addEventListener('dragend', () => {
      this.dragging = null;
      for (const row of this.tree.querySelectorAll('[data-drop]')) row.removeAttribute('data-drop');
    });

    return grip;
  }

  /**
   * A row takes a drop: the upper half puts the dragged circle beside this one,
   * the lower half puts it inside. Refusals are the pure moves' — a drop that
   * would nest four deep, or fold a circle into its own subtree, comes back
   * empty and is said out loud rather than silently doing nothing.
   */
  private wireDropTarget(row: HTMLElement, id: string): void {
    row.addEventListener('dragover', (event) => {
      if (!this.dragging || this.dragging === id) return;
      event.preventDefault();
      event.stopPropagation();
      row.dataset.drop = dropWhere(event as DragEvent, row);
    });
    row.addEventListener('dragleave', () => row.removeAttribute('data-drop'));
    row.addEventListener('drop', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const source = this.dragging;
      const where = dropWhere(event as DragEvent, row);
      row.removeAttribute('data-drop');
      if (!source || source === id) return;
      const moves = dropOn(buildCircleTree(this.options.tags()), source, id, where);
      if (moves.length === 0) {
        toast('Circles go three deep, and none can go inside itself.', 'error');
        return;
      }
      void this.applyMoves(moves);
    });
  }

  /** Which pure move an arrow key asks for, or `null` for a key we do not own. */
  private movesFor(tree: CircleNode, id: string, key: string): TagMove[] | null {
    switch (key) {
      case 'ArrowUp':
        return moveWithinParent(tree, id, -1);
      case 'ArrowDown':
        return moveWithinParent(tree, id, 1);
      case 'ArrowRight':
        return indentUnderPrevious(tree, id);
      case 'ArrowLeft':
        return outdentToGrandparent(tree, id);
      default:
        return null;
    }
  }

  private async applyMoves(moves: TagMove[]): Promise<void> {
    try {
      for (const move of moves) await api.patchTag(this.options.calendarId, move.id, move.patch);
      await this.options.reload();
      this.paint();
    } catch (err) {
      sayCircleError(err, 'That move did not save.');
      await this.options.reload().catch(() => {});
      this.paint();
    }
  }

  // --- the view's half ------------------------------------------------------

  private setNode(id: string, patch: { placement?: ChildPlacement; visible?: boolean }): void {
    const next: ViewLayout = withNode(this.options.working.current, id, patch);
    // The scene hears at once and tweens; the view hears nothing until Save.
    // `animate` is the big bang of docs/CIRCLES.md §3. The tree repaints from
    // the watcher, which is also what catches a change made from the bar.
    this.options.working.set(next, { animate: true });
  }

  // --- the circle's half ----------------------------------------------------

  private async patchTag(
    tag: Tag,
    patch: Parameters<typeof api.patchTag>[2],
    revert?: () => void,
  ): Promise<void> {
    try {
      await api.patchTag(this.options.calendarId, tag.id, patch);
      await this.options.reload();
      this.paintTree();
    } catch (err) {
      revert?.();
      sayCircleError(err, 'That change did not save.');
    }
  }

  private async addCircle(parentId: string | null): Promise<void> {
    const siblings = this.options.tags().filter((t) => (t.parentId ?? null) === parentId);
    const name = uniqueName(siblings.map((t) => t.name));
    try {
      const created = await api.createTag(this.options.calendarId, {
        name,
        parentId,
        colour: nextColour(CIRCLE_COLOURS, this.options.tags().map((t) => t.colour)),
        sort: siblings.length,
      });
      await this.options.reload();
      this.paint();
      // Straight into the name field: the name it was given is a placeholder.
      const input = this.tree.querySelector<HTMLInputElement>(
        `[data-circle="${cssEscape(created.id)}"] input.circle__name`,
      );
      input?.focus();
      input?.select();
    } catch (err) {
      sayCircleError(err, 'That circle was not made.');
    }
  }

  private async deleteCircle(node: CircleNode): Promise<void> {
    const tag = node.tag!;
    const inside = node.children.length;
    const yes = await confirmDialog({
      title: `Delete ${tag.name}?`,
      description: inside
        ? `The entries stay on the ring, and the ${inside === 1 ? 'circle' : `${inside} circles`} inside move up a level.`
        : 'The entries in it stay on the ring; they go back to being in no circle.',
      confirmLabel: 'Delete circle',
      danger: true,
    });
    if (!yes) return;
    try {
      await api.deleteTag(this.options.calendarId, tag.id);
      await this.options.reload();
      this.paint();
      toast(`Deleted ${tag.name}.`);
    } catch (err) {
      sayCircleError(err, 'That circle was not deleted.');
    }
  }
}

// --- small helpers -----------------------------------------------------------

/**
 * The worker's refusals are codes, and a few of them deserve a better answer
 * than the sentence the API sends back. `TAG_DEPTH` and its neighbours say what
 * happened; the messages in `circles.ts` say what to do about it, so they win
 * where there is one and the server's own words are the fallback.
 */
function sayCircleError(err: unknown, fallback: string): void {
  const known = circleErrorMessage(codeOf(err));
  if (known) toast(known, 'error', 7000);
  else toastError(err, fallback);
}

function codeOf(err: unknown): string | undefined {
  return err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : undefined;
}

/** Why an arrow key did nothing, in the words the worker would have used. */
function refusalFor(key: string, tree: CircleNode, node: CircleNode): string {
  if (key === 'ArrowRight') {
    const parent = node.parentId ? findNode(tree, node.parentId) : undefined;
    const siblings = parent?.children ?? [];
    const at = siblings.findIndex((n) => n.id === node.id);
    if (at <= 0) return 'Nothing above this one to go inside.';
    return circleErrorMessage('TAG_DEPTH')!;
  }
  if (key === 'ArrowLeft') return 'This circle is already at the top level.';
  return key === 'ArrowUp' ? 'Already first here.' : 'Already last here.';
}

/** ←/↑ step back, →/↓ step on, Home/End jump; anything else is not ours. */
function arrowDelta(key: string): number {
  switch (key) {
    case 'ArrowLeft':
    case 'ArrowUp':
      return -1;
    case 'ArrowRight':
    case 'ArrowDown':
      return 1;
    case 'Home':
      return -PLACEMENTS.length;
    case 'End':
      return PLACEMENTS.length;
    default:
      return 0;
  }
}

/**
 * Every circle `id` could be moved into: not itself, not anything inside it,
 * not the parent it already has, and nowhere it would be nested too deep. The
 * root counts, for a circle that is not already at the top level.
 */
export function moveTargets(tree: CircleNode, id: string): CircleNode[] {
  return flattenTree(tree).filter((n) => dropOn(tree, id, n.id, 'into').length > 0);
}

/** Upper half of a row: drop beside it. Lower half: drop inside it. */
function dropWhere(event: DragEvent, row: HTMLElement): 'into' | 'after' {
  const box = row.getBoundingClientRect();
  return event.clientY - box.top > box.height / 2 ? 'into' : 'after';
}

function uniqueName(taken: readonly string[]): string {
  const used = new Set(taken.map((n) => n.toLowerCase()));
  if (!used.has('new circle')) return 'New circle';
  for (let i = 2; ; i++) {
    const candidate = `New circle ${i}`;
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
}

/** `CSS.escape` where there is one; a tag id is `[A-Za-z0-9_]` anyway. */
function cssEscape(value: string): string {
  return typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(value)
    : value.replace(/["\\]/g, '\\$&');
}

// --- glyphs ------------------------------------------------------------------

const SVG_NS = 'http://www.w3.org/2000/svg';

function svg(size: number, ...children: SVGElement[]): SVGSVGElement {
  const node = document.createElementNS(SVG_NS, 'svg');
  node.setAttribute('viewBox', '0 0 16 16');
  node.setAttribute('width', String(size));
  node.setAttribute('height', String(size));
  node.setAttribute('aria-hidden', 'true');
  node.setAttribute('focusable', 'false');
  for (const child of children) node.appendChild(child);
  return node;
}

function circle(cx: number, cy: number, r: number, attrs: Record<string, string> = {}): SVGCircleElement {
  const node = document.createElementNS(SVG_NS, 'circle');
  node.setAttribute('cx', String(cx));
  node.setAttribute('cy', String(cy));
  node.setAttribute('r', String(r));
  node.setAttribute('fill', 'none');
  node.setAttribute('stroke', 'currentColor');
  for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, value);
  return node;
}

function path(d: string, attrs: Record<string, string> = {}): SVGPathElement {
  const node = document.createElementNS(SVG_NS, 'path');
  node.setAttribute('d', d);
  node.setAttribute('fill', 'none');
  node.setAttribute('stroke', 'currentColor');
  for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, value);
  return node;
}

/** Marks a circle that has a link of its own, so the panel says so without opening it. */
function linkedBadge(name: string): HTMLElement {
  return el(
    'span',
    {
      class: 'circle__linked',
      title: 'Has a link of its own',
      role: 'img',
      'aria-label': `${name} has a link of its own`,
    },
    linkGlyph(),
  );
}

function linkGlyph(): SVGSVGElement {
  return svg(
    16,
    path('M6.6 9.4 9.4 6.6', { 'stroke-width': '1.4', 'stroke-linecap': 'round' }),
    path('M5.2 10.8 4 12a2.3 2.3 0 0 1-3.2-3.2l2.4-2.4A2.3 2.3 0 0 1 6.4 6.4', {
      'stroke-width': '1.4',
      'stroke-linecap': 'round',
    }),
    path('M10.8 5.2 12 4a2.3 2.3 0 0 1 3.2 3.2l-2.4 2.4a2.3 2.3 0 0 1-3.2 0', {
      'stroke-width': '1.4',
      'stroke-linecap': 'round',
    }),
  );
}

/** A circle's mark: a ring in its own colour, or a hairline where it inherits. */
function ringGlyph(colour: string): SVGSVGElement {
  const ring = circle(8, 8, 5.5, { 'stroke-width': '2' });
  if (colour) {
    ring.setAttribute('stroke', colour);
    ring.setAttribute('fill', colour);
    ring.setAttribute('fill-opacity', '0.22');
  } else {
    ring.setAttribute('stroke-dasharray', '2 2');
  }
  return svg(16, ring);
}

function eyeGlyph(visible: boolean): SVGSVGElement {
  const lid = path('M1.5 8s2.6-4 6.5-4 6.5 4 6.5 4-2.6 4-6.5 4S1.5 8 1.5 8Z', {
    'stroke-width': '1.3',
  });
  const pupil = circle(8, 8, 1.9, { 'stroke-width': '1.3' });
  return visible
    ? svg(16, lid, pupil)
    : svg(16, lid, pupil, path('M3 13 13 3', { 'stroke-width': '1.3', 'stroke-linecap': 'round' }));
}

function chevronGlyph(open: boolean): SVGSVGElement {
  return svg(
    16,
    path(open ? 'M4 10.5 8 6l4 4.5' : 'M6 4l4.5 4L6 12', {
      'stroke-width': '1.4',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    }),
  );
}

function gripGlyph(): SVGSVGElement {
  return svg(
    16,
    path('M5 4.5h6M5 8h6M5 11.5h6', { 'stroke-width': '1.3', 'stroke-linecap': 'round' }),
  );
}

/**
 * Where each placement puts the circle, drawn small: a band inside one ring,
 * rings sharing a centre, and little rings out on an orbit.
 */
function placementGlyph(placement: ChildPlacement): SVGSVGElement {
  if (placement === 'band') {
    return svg(16, circle(8, 8, 6, { 'stroke-width': '1.2' }), circle(8, 8, 4, { 'stroke-width': '2.4', 'stroke-opacity': '0.45' }));
  }
  if (placement === 'nested') {
    return svg(
      16,
      circle(8, 8, 6.5, { 'stroke-width': '1.2' }),
      circle(8, 8, 4, { 'stroke-width': '1.2' }),
      circle(8, 8, 1.6, { 'stroke-width': '1.2' }),
    );
  }
  return svg(
    16,
    circle(8, 8, 2.4, { 'stroke-width': '1.2', 'stroke-dasharray': '1.5 1.5' }),
    circle(8, 2.6, 2.1, { 'stroke-width': '1.2' }),
    circle(2.9, 11, 2.1, { 'stroke-width': '1.2' }),
    circle(13.1, 11, 2.1, { 'stroke-width': '1.2' }),
  );
}
